/**
 * VS-1 — pure logic for a volunteer responding to a service assignment.
 *
 * This module is the single source of truth for the accept/decline transition
 * and for building the `respond_to_volunteer_assignment` RPC arguments. Keeping
 * it pure (no React, no Supabase) lets us unit-test the behaviour the security
 * definer RPC enforces server-side, and reuse it for optimistic UI updates.
 *
 * A decline does NOT touch the position — the slot "reopens" purely because a
 * `declined` row is excluded from the staffing tally (see `tallyStaffing` in
 * `service-staffing.ts`, which the slot-reopen test exercises).
 */

import type { AssignmentStatus } from "@/lib/service-staffing";

export type AssignmentResponseAction = "accept" | "decline";

/** Product-defined reasons a volunteer can give when declining. */
export type DeclineReason = "unavailable" | "not_qualified" | "other";

export const DECLINE_REASONS: ReadonlyArray<{ value: DeclineReason; label: string }> = [
  { value: "unavailable", label: "Can’t make it" },
  { value: "not_qualified", label: "Not qualified for this role" },
  { value: "other", label: "Other" },
] as const;

export interface DeclineDetails {
  reason: DeclineReason | null;
  note?: string | null;
  /** Volunteer asked the coordinator to find a substitute. */
  requestSub?: boolean;
}

/** Argument shape for `supabase.rpc("respond_to_volunteer_assignment", …)`. */
export interface RespondRpcArgs {
  p_assignment_id: string;
  p_action: AssignmentResponseAction;
  p_decline_reason: DeclineReason | null;
  p_response_note: string | null;
  p_request_sub: boolean;
}

/** The columns the RPC writes — mirrored here for optimistic updates + tests. */
export interface AssignmentResponseFields {
  status: Extract<AssignmentStatus, "confirmed" | "declined">;
  confirmed_at: string | null;
  responded_at: string;
  decline_reason: DeclineReason | null;
  response_note: string | null;
  requested_sub: boolean;
}

const normalizeNote = (note?: string | null): string | null => {
  const trimmed = (note ?? "").trim();
  return trimmed.length > 0 ? trimmed : null;
};

export function buildRespondRpcArgs(
  assignmentId: string,
  action: AssignmentResponseAction,
  decline?: DeclineDetails,
): RespondRpcArgs {
  if (action === "accept") {
    return {
      p_assignment_id: assignmentId,
      p_action: "accept",
      p_decline_reason: null,
      p_response_note: null,
      p_request_sub: false,
    };
  }
  return {
    p_assignment_id: assignmentId,
    p_action: "decline",
    p_decline_reason: decline?.reason ?? null,
    p_response_note: normalizeNote(decline?.note),
    p_request_sub: decline?.requestSub ?? false,
  };
}

export function computeAssignmentResponse(
  action: AssignmentResponseAction,
  decline: DeclineDetails | undefined,
  now: string,
): AssignmentResponseFields {
  if (action === "accept") {
    return {
      status: "confirmed",
      confirmed_at: now,
      responded_at: now,
      decline_reason: null,
      response_note: null,
      requested_sub: false,
    };
  }
  return {
    status: "declined",
    confirmed_at: null,
    responded_at: now,
    decline_reason: decline?.reason ?? null,
    response_note: normalizeNote(decline?.note),
    requested_sub: decline?.requestSub ?? false,
  };
}
